import Box from '@sendsprint/ui-react/dist/components/Box';
import React from 'react';
import cs from 'classnames';
import AmountStep from '../amountStep';
import ScheduleStep from '../scheduleStep';
import TransferInformation from '../transferInformation';
import { UseMultiFormResponse } from '../../state/useMultiForm';
import { FormVariantsI } from '../../state';
import lazyWithPreload from '@src/utils/lazyWithPreload';
import SuspenseWrapper from '@src/components/suspenseWrapper';
import Loader from '@src/components/loader';
import ErrorFallback from '@src/components/errorFallback';

export const AddressStep = lazyWithPreload(() => import('../addressStep'));
export const PhoneNumberUpdate = lazyWithPreload(() => import('../phoneNumberUpdate'));
export const RecipientStep = lazyWithPreload(() => import('../recipientStep'));
export const SecurityQuestionStep = lazyWithPreload(() => import('../securityQuestionStep'));
export const ConfirmPaymentStep = lazyWithPreload(() => import('../confirmPaymentStep'));
export const ScheduleSummary = lazyWithPreload(
  () => import('../confirmPaymentStep/scheduleSummary')
);

interface Props {
  multiFormResponse: UseMultiFormResponse;
  variant: FormVariantsI;
}

const SendMoneySteps = ({ multiFormResponse, variant }: Props) => {
  const {
    state,
    markCurrentStepAsDirty,
    amountStepSuccessHandler,
    recipientStepSuccessHandler,
    recipientStepInvalidDataHandler,
    yourAddressSuccessHandler,
    updatePhoneNumberSuccessHandler,
    securityQuestionStepSuccessHandler,
    confirmStepInvalidDataHandler,
    reEditStepHandler,
    resetStateHandler,
    markPageAsClean,
    loadVoucherSuccessHandler,
    TransferScheduleSuccessHandler,
    markPageAllAsClean
  } = multiFormResponse;
  return (
    <Box onChange={markCurrentStepAsDirty} className="ss-flex ss-items-start ss-gap-8">
      <Box className="ss-flex-1">
        <SuspenseWrapper
          suspenseFallback={<Loader />}
          errorFallback={(errObj) => (
            <Box className="ss-py-10">
              <ErrorFallback {...errObj} text="Error loading form" />
            </Box>
          )}>
          {variant === 'transfer schedule' &&
            state.currentIndex === state.stepsIndexMap.schedule && (
              <ScheduleStep
                formData={state.transferScheduleData}
                onSubmitSuccess={TransferScheduleSuccessHandler}
              />
            )}
          {state.currentIndex === state.stepsIndexMap.amount && (
            <AmountStep
              state={state}
              formData={state.amountFormData}
              prefillFormData={state.prefillForm}
              onStepIsDirty={markCurrentStepAsDirty}
              onSubmitSuccess={amountStepSuccessHandler}
              isAddressStepShown={state.steps[state.stepsIndexMap.address].isVisible}
              isPhoneNumberStepShown={state.steps[state.stepsIndexMap.updatePhoneNumber].isVisible}
            />
          )}
          {state.currentIndex === state.stepsIndexMap.address && (
            <AddressStep
              formData={state.addressFormData}
              onSubmitSuccess={yourAddressSuccessHandler}
              editMode={state.editMode}
            />
          )}
          {state.currentIndex === state.stepsIndexMap.updatePhoneNumber && (
            <PhoneNumberUpdate
              formData={state.updatePhoneNumberData}
              onSubmitSuccess={updatePhoneNumberSuccessHandler}
              editMode={state.editMode}
            />
          )}
          {state.currentIndex === state.stepsIndexMap.recipient && (
            <RecipientStep
              recipientType={state?.amountFormData?.recipientType}
              selectedRecipient={state.selectedRecipient}
              onSelectRecipient={recipientStepSuccessHandler}
              onInvalidData={recipientStepInvalidDataHandler}
              editMode={state.editMode}
              isSecurityQuestionShown={state.steps[state.stepsIndexMap.securityQuestion].isVisible}
            />
          )}
          {state.currentIndex === state.stepsIndexMap.securityQuestion && (
            <SecurityQuestionStep
              formData={state.securityQuestionFormData}
              onSuccess={securityQuestionStepSuccessHandler}
            />
          )}
          {state.currentIndex === state.stepsIndexMap.confirmPayment && (
            <ConfirmPaymentStep
              amountFormData={state.amountFormData}
              selectedRecipient={state.selectedRecipient}
              securityQuestionFormData={state.securityQuestionFormData}
              transferScheduleData={state.transferScheduleData}
              onInvalidData={confirmStepInvalidDataHandler}
              onReEditStep={reEditStepHandler}
              onCancelTransfer={resetStateHandler}
              onPaymentSuccess={markPageAsClean}
              state={state}
              loadVoucherSuccessHandler={loadVoucherSuccessHandler}
              paymentMethodData={state.paymentMethodData}
              variant={variant}
              markPageAsClean={markPageAllAsClean}
            />
          )}
        </SuspenseWrapper>
      </Box>
      <Box
        className={cs('ss-hidden ss-space-y-5', {
          'ss-w-[350px] xl:ss-block':
            state.currentIndex !== state.stepsIndexMap.confirmPayment &&
            state.currentIndex !== state.stepsIndexMap.schedule,
          'ss-hidden':
            state.currentIndex === state.stepsIndexMap.confirmPayment ||
            state.currentIndex === state.stepsIndexMap.schedule
        })}>
        {variant === 'transfer schedule' && (
          <SuspenseWrapper returnNullErrorFallback>
            <ScheduleSummary
              transferScheduleData={state.transferScheduleData}
              handleEdit={() => reEditStepHandler('schedule')}
            />
          </SuspenseWrapper>
        )}
        <TransferInformation state={state} handleEdit={() => reEditStepHandler('amount')} />
      </Box>
    </Box>
  );
};

export default SendMoneySteps;
